/**
 * 本地草稿持久化（localStorage）
 * 目标：刷新页面不丢失"标书解析/目录/正文"关键中间结果，方便调试与续写。
 *
 * 注意：localStorage 容量有限（通常 5-10MB），大正文可能触发 QUOTA_EXCEEDED_ERR。
 * 这里做了 try/catch，失败时不影响主流程。
 *
 * 每个用户的草稿数据通过 userId 隔离，key 格式为 yibiao:draft:v1:{userId}
 */

import type { AppState, OutlineItem } from '../types';

const DRAFT_KEY_PREFIX = 'yibiao:draft:v1';
const CONTENT_BY_ID_KEY_PREFIX = 'yibiao:contentById:v1';

export type DraftState = Pick<
  AppState,
  'config' | 'currentStep' | 'currentProject' | 'fileContent' | 'fileName' | 'projectOverview' | 'techRequirements' | 'analysisResults' | 'outlineData' | 'bizOutlineData' | 'outlineSource' | 'generationMode'
>;

/** 清除当前用户的 contentById 缓存（切换/创建项目时调用） */
export function clearContentByIdForUser(userId: number) {
  try {
    const key = `${CONTENT_BY_ID_KEY_PREFIX}:${userId}`;
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('清除 contentById 失败:', e);
  }
}

export type ContentById = Record<string, string>; // 章节id -> content

const safeJsonParse = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

/**
 * 创建按用户隔离的 draftStorage 实例
 */
export function createDraftStorage(userId: number) {
  const DRAFT_KEY = `${DRAFT_KEY_PREFIX}:${userId}`;
  const CONTENT_BY_ID_KEY = `${CONTENT_BY_ID_KEY_PREFIX}:${userId}`;

  return {
    loadDraft(): Partial<DraftState> | null {
      return safeJsonParse<Partial<DraftState>>(localStorage.getItem(DRAFT_KEY));
    },

    saveDraft(partial: Partial<DraftState>) {
      try {
        const prev = safeJsonParse<Partial<DraftState>>(localStorage.getItem(DRAFT_KEY)) || {};
        const next = { ...prev, ...partial };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
      } catch (e) {
        console.warn('保存草稿失败（可能是 localStorage 空间不足）:', e);
      }
    },

    clearAll() {
      try {
        localStorage.removeItem(DRAFT_KEY);
        localStorage.removeItem(CONTENT_BY_ID_KEY);
      } catch (e) {
        console.warn('清空 localStorage 失败:', e);
      }
    },

    loadContentById(): ContentById {
      return safeJsonParse<ContentById>(localStorage.getItem(CONTENT_BY_ID_KEY)) || {};
    },

    saveContentById(contentById: ContentById) {
      try {
        localStorage.setItem(CONTENT_BY_ID_KEY, JSON.stringify(contentById));
      } catch (e) {
        console.warn('保存正文内容失败（可能是 localStorage 空间不足）:', e);
      }
    },

    /**
     * 获取章节的稳定标识符，优先使用 stable_id，回退到 id。
     */
    _contentKey(itemOrId: OutlineItem | string): string {
      if (typeof itemOrId === 'string') return itemOrId;
      return itemOrId.stable_id || itemOrId.id;
    },

    upsertChapterContent(chapterIdOrItem: string | OutlineItem, content: string) {
      try {
        const map = this.loadContentById();
        map[this._contentKey(chapterIdOrItem)] = content;
        this.saveContentById(map);
      } catch (e) {
        console.warn('保存章节内容失败:', e);
      }
    },

    removeChapterContent(chapterId: string) {
      try {
        const map = this.loadContentById();
        delete map[chapterId];
        this.saveContentById(map);
      } catch (e) {
        console.warn('删除章节内容失败:', e);
      }
    },

    /**
     * 按当前 outline 的叶子节点过滤 contentById，使用 stable_id 匹配避免目录变更后错误回填。
     * 
     * 重要：仅当节点缺少 stable_id 时才回退到 node_id 匹配，防止跨项目内容泄漏。
     * 所有新项目的节点都有 stable_id（UUID），旧项目的 node_id（如 "1"）不应与任何新项目匹配。
     */
    filterContentByOutlineLeaves(outline: OutlineItem[]): ContentById {
      const map = this.loadContentById();
      if (Object.keys(map).length === 0) return {};
      
      const leafIds = new Set<string>();
      const walk = (items: OutlineItem[]) => {
        items.forEach((it) => {
          if (it.stable_id) {
            // 有 stable_id：只按 stable_id 匹配，不使用 node_id（避免跨项目泄漏）
            leafIds.add(it.stable_id);
          } else {
            // 无 stable_id（旧数据兼容）：回退到 node_id
            leafIds.add(it.id);
          }
          if (!it.children || it.children.length === 0) {
            return;
          }
          walk(it.children);
        });
      };
      walk(outline);

      const filtered: ContentById = {};
      Object.keys(map).forEach((key) => {
        if (leafIds.has(key)) filtered[key] = map[key];
      });
      return filtered;
    },
  };
}

/** 兼容旧版：无用户隔离的全局实例（仅用于过渡/清理） */
export const draftStorage = createDraftStorage(0);


